/**
 * A tiny key-value store on IndexedDB (the browser's own database), used to keep Bungie's game database between
 * visits so it is downloaded once per Destiny update, not on every page load. Works in the page and in workers.
 * Every call fails soft: a private window or blocked storage just means things are downloaded again.
 */

const DB = 'd2companion';
const STORE = 'kv';

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return opening;
}

function run<T>(mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest): Promise<T | undefined> {
  return open()
    .then((db) => new Promise<T | undefined>((resolve) => {
      const req = work(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => resolve(undefined);
    }))
    .catch(() => undefined);
}

export const idbGet = <T>(key: string) => run<T>('readonly', (s) => s.get(key));
export const idbSet = (key: string, value: unknown) => run('readwrite', (s) => s.put(value, key));
export const idbDelete = (key: string) => run('readwrite', (s) => s.delete(key));
export const idbKeys = () => run<IDBValidKey[]>('readonly', (s) => s.getAllKeys()).then((k) => (k ?? []).map(String));
